import React, { useState } from 'react';
import { useParams } from 'react-router-dom';
import { toast } from 'sonner';
import { getSupabaseClient } from '../../lib/supabase';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Textarea } from '../../components/ui/textarea';
import { Star, CheckCircle2, Loader2, Sparkles, Heart } from 'lucide-react';

export default function RateService() {
  const { bookingId } = useParams<{ bookingId: string }>();
  const supabase = getSupabaseClient();

  const [rating, setRating] = useState<number>(5);
  const [hoverRating, setHoverRating] = useState<number | null>(null);
  const [reviewText, setReviewText] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const starLabels: Record<number, string> = {
    1: 'Poor',
    2: 'Fair',
    3: 'Good',
    4: 'Very Good',
    5: 'Excellent!'
  };

  const activeRating = hoverRating || rating;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!supabase) {
      toast.error('Unable to connect to database');
      return;
    }

    if (!bookingId) {
      toast.error('Invalid booking link. Booking ID is missing.');
      return;
    }

    if (!rating || rating < 1 || rating > 5) {
      toast.error('Please select a star rating between 1 and 5');
      return;
    }

    setSubmitting(true);
    try {
      const { error } = await supabase.rpc('submit_booking_rating', {
        p_booking_id: bookingId,
        p_rating: rating,
        p_review_text: reviewText.trim() || null
      });

      if (error) {
        throw error;
      }

      setSubmitted(true);
      toast.success('Thank you for rating your service!');
    } catch (err: any) {
      console.error('Error submitting booking rating:', err);
      // Surface exact backend error message
      toast.error(err.message || 'Failed to submit rating');
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-slate-900 via-slate-900 to-slate-950 text-white flex items-center justify-center p-4">
        <Card className="max-w-md w-full bg-slate-800/90 border-slate-700/80 rounded-3xl shadow-2xl p-6 sm:p-8 text-center backdrop-blur-md animate-in zoom-in-95 duration-200">
          <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-emerald-500/20 text-emerald-400 flex items-center justify-center border border-emerald-500/30">
            <CheckCircle2 size={36} />
          </div>
          <h1 className="text-2xl font-bold text-white mb-2">Thank You!</h1>
          <p className="text-sm text-slate-300 mb-6 leading-relaxed">
            Your feedback helps us continuously improve the quality of service. We have recorded your rating.
          </p>

          <div className="p-4 rounded-2xl bg-slate-900/60 border border-slate-700/60 inline-flex items-center gap-1.5 mb-6">
            {[1, 2, 3, 4, 5].map(star => (
              <Star
                key={star}
                size={22}
                className={star <= rating ? 'fill-amber-400 text-amber-400' : 'text-slate-600'}
              />
            ))}
          </div>

          <div className="text-xs text-slate-500 flex items-center justify-center gap-1.5">
            <Heart size={14} className="text-rose-500 fill-rose-500" />
            <span>Thank you for choosing BahiBox Daily Services</span>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-900 via-slate-900 to-slate-950 text-white flex flex-col justify-between p-4 sm:p-6">
      <div className="max-w-md w-full mx-auto my-auto py-6">
        <Card className="bg-slate-800/90 border-slate-700/80 rounded-3xl shadow-2xl backdrop-blur-md overflow-hidden">
          <div className="p-6 sm:p-8 text-center border-b border-slate-700/60 bg-gradient-to-b from-slate-800 to-slate-800/40">
            <div className="inline-flex p-3 rounded-2xl bg-amber-400/10 text-amber-400 border border-amber-400/20 mb-3 shadow-inner">
              <Sparkles size={28} />
            </div>
            <h1 className="text-xl sm:text-2xl font-bold text-white">
              How was your service?
            </h1>
            <p className="text-xs text-slate-400 mt-1">
              Please rate your experience with our professional
            </p>
          </div>

          <CardContent className="p-6 sm:p-8">
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Star Rating Picker */}
              <div className="text-center">
                <div className="flex items-center justify-center gap-2 mb-2">
                  {[1, 2, 3, 4, 5].map(star => (
                    <button
                      key={star}
                      type="button"
                      onClick={() => setRating(star)}
                      onMouseEnter={() => setHoverRating(star)}
                      onMouseLeave={() => setHoverRating(null)}
                      className="p-1 rounded-xl hover:scale-110 active:scale-95 transition-transform focus:outline-none"
                      title={`${star} Star${star > 1 ? 's' : ''}`}
                    >
                      <Star
                        size={36}
                        className={`transition-colors ${
                          star <= activeRating
                            ? 'fill-amber-400 text-amber-400 drop-shadow-[0_0_8px_rgba(251,191,36,0.5)]'
                            : 'text-slate-600 hover:text-slate-500'
                        }`}
                      />
                    </button>
                  ))}
                </div>
                <span className="text-sm font-bold text-amber-300 min-h-[20px] block transition-all">
                  {starLabels[activeRating] || ''}
                </span>
              </div>

              {/* Optional Review Text */}
              <div>
                <label className="block text-xs font-semibold text-slate-300 mb-1.5 uppercase tracking-wider">
                  Write a review <span className="text-slate-500 lowercase">(optional)</span>
                </label>
                <Textarea
                  value={reviewText}
                  onChange={e => setReviewText(e.target.value)}
                  placeholder="Tell us what you liked, or how we can improve..."
                  rows={3}
                  className="bg-slate-900/80 border-slate-700 text-white placeholder:text-slate-500 rounded-xl text-xs resize-none"
                />
              </div>

              {/* Submit Button */}
              <Button
                type="submit"
                disabled={submitting}
                className="w-full h-12 bg-primary hover:bg-primary/90 text-white font-bold rounded-xl text-sm shadow-lg shadow-primary/20 transition-all flex items-center justify-center gap-2"
              >
                {submitting ? (
                  <>
                    <Loader2 size={16} className="animate-spin" />
                    <span>Submitting Rating...</span>
                  </>
                ) : (
                  <span>Submit Rating</span>
                )}
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>

      <footer className="text-center text-xs text-slate-500 py-3">
        BahiBox Daily Services &bull; Customer Service Rating
      </footer>
    </div>
  );
}
